import axios from 'axios';

export type Lang = 'pt' | 'en' | 'es';
export type Side = 'single' | 'front' | 'back';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';
export type ChecklistStatus = 'awaiting_client' | 'submitted' | 'completed' | 'revoked' | 'expired';

export const VISA_TYPES = [
  'trabalho_subordinado',
  'procura_trabalho',
  'estudo',
  'empreendedorismo_d2',
  'nomade_digital_d8',
  'rendimentos_d7',
  'reagrupamento_familiar',
  'nacionalidade',
  'outro',
] as const;
export type VisaType = (typeof VISA_TYPES)[number];

export interface ItemText {
  label: string;
  description: string;
}

export interface ChecklistFile {
  _id: string;
  side: Side;
  mime: string;
  size: number;
  uploadedBy: 'client' | 'admin';
  uploadedAt: string;
  url: string;
}

export interface ChecklistItem {
  key: string;
  sides: 'single' | 'front_back';
  required: boolean;
  acceptPdf: boolean;
  i18n: Record<Lang, ItemText>;
  clientChecked: boolean;
  clientCheckedAt?: string | null;
  files: ChecklistFile[];
  review: { status: ReviewStatus; note: string; at?: string | null };
}

export interface ChecklistProgress {
  total: number;
  uploaded: number;
  checked: number;
  approved: number;
  rejected: number;
  missingRequired: number;
}

export interface ChecklistConsent {
  acceptedAt: string;
  version: string;
  lang: string;
  ip: string;
  userAgent: string;
  retentionDays: number | null;
}

export interface AdminChecklist {
  _id: string;
  consent: ChecklistConsent | null;
  retentionDays: number;
  retentionIsDefault: boolean;
  completedAt?: string | null;
  filesPurgeAt?: string | null;
  filesPurgedAt?: string | null;
  clienteId: string;
  clienteNome?: string;
  visaType: VisaType;
  lang: Lang;
  status: ChecklistStatus;
  expiresAt: string;
  clientEmailHint: string;
  adminNotes: string;
  submittedAt?: string | null;
  lastClientActivityAt?: string | null;
  createdAt: string;
  formUrl: string;
  progress: ChecklistProgress;
  items: ChecklistItem[];
}

export interface TemplateItem {
  key: string;
  sides: 'single' | 'front_back';
  required: boolean;
  acceptPdf: boolean;
  i18n: Record<Lang, ItemText>;
}

export interface ChecklistTemplate {
  visaType: VisaType;
  items: TemplateItem[];
  active: boolean;
}

export function sidesOf(item: { sides: 'single' | 'front_back' }): Side[] {
  return item.sides === 'front_back' ? ['front', 'back'] : ['single'];
}

export function itemText(item: { i18n: Record<Lang, ItemText> }, lang: string): ItemText {
  const l = (['pt', 'en', 'es'].includes(lang) ? lang : 'pt') as Lang;
  return {
    label: item.i18n?.[l]?.label || item.i18n?.pt?.label || '',
    description: item.i18n?.[l]?.description || item.i18n?.pt?.description || '',
  };
}

/** Caminho de API devolvido pelo backend (/api/...) → URL utilizável no navegador (dev, raiz ou subpath). */
export function apiFileUrl(apiPath: string): string {
  const base = String(axios.defaults.baseURL || '/api').replace(/\/api\/?$/, '');
  return `${base}${apiPath}`;
}

export function absoluteUrl(url: string): string {
  return new URL(url, window.location.origin).href;
}

const MAX_DIMENSION = 2000;

/**
 * Reduz fotos grandes da câmera (12+ MP) para no máximo 2000 px em JPEG antes do envio:
 * acelera o upload no 4G e mantém o arquivo bem abaixo do limite de 8 MB. PDFs passam intactos.
 */
export async function prepareUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const bitmap = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 2.5 * 1024 * 1024 && file.type !== 'image/heic') return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

export function errorMessage(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}
